/**
 * Read EVERY row of a PostgREST query, page by page (field test 2026-10-20,
 * audit D AD-8).
 *
 * PostgREST caps every response at `max_rows` (Supabase default 1000) and
 * says nothing when it does: a `select('*')` of a 1,001-row table returns
 * 1,000 rows and no error. The job pickers load the whole live catalog
 * (963 live rows at audit time) for Quick Search and derive the category list
 * from it, so once the catalog grows past the cap, jobs late in the sort order
 * — and whole categories — would silently disappear from the pickers.
 *
 * Rule: ask for the exact count, then fetch `range(from, to)` pages until the
 * count is reached. `from` advances by the rows actually received, so a server
 * cap SMALLER than our page size (a project configured to 500) still reads
 * everything. Without a count, read until an empty page. Any page error →
 * `{ data: null, error }` (never a silently partial list — the same contract
 * as one failed supabase-js call).
 *
 * Pure: no client import, so it is unit-testable and usable from scripts.
 * The caller supplies a deterministic total order (e.g. label, then id).
 */

export const POSTGREST_DEFAULT_MAX_ROWS = 1000;

/** The part of a PostgREST / supabase-js error every caller reads. */
export type PageError = { message: string } | null;

export interface PageResponse<T> {
  data: T[] | null;
  error: PageError;
  count?: number | null;
}

export interface FetchAllOptions {
  /** Rows requested per page (default 1000 = the PostgREST default cap). */
  pageSize?: number;
  /** Hard stop against a misbehaving server (default 100 pages). */
  maxPages?: number;
}

export async function fetchAllPages<T>(
  fetchPage: (from: number, to: number) => PromiseLike<PageResponse<T>>,
  options: FetchAllOptions = {},
): Promise<{ data: T[] | null; error: PageError }> {
  const pageSize = Math.max(1, Math.floor(options.pageSize ?? POSTGREST_DEFAULT_MAX_ROWS));
  const maxPages = Math.max(1, Math.floor(options.maxPages ?? 100));
  const rows: T[] = [];
  let total: number | null = null;

  for (let page = 0; page < maxPages; page++) {
    const from = rows.length;
    const res = await fetchPage(from, from + pageSize - 1);
    if (res.error) return { data: null, error: res.error };
    if (typeof res.count === 'number' && Number.isFinite(res.count)) total = res.count;
    const got = res.data ?? [];
    rows.push(...got);
    if (got.length === 0) return { data: rows, error: null };
    if (total !== null && rows.length >= total) return { data: rows, error: null };
  }
  return { data: null, error: new Error(`fetchAllPages: more than ${maxPages} pages of ${pageSize} rows`) };
}
